/// <amd-module name="VSS/Features/Charts/Contracts" />

import { IVssPageContext } from "VSS/Platform/Context";

/** Placeholder for the Host to transport user-customizable coloring options. */
export interface ColorCustomizationOptions {
    /** Opt-in to use subdued Coloring.
     Interpreted as false by default. */
    useSubduedPalette?: boolean;

    /** A list of user-customized colorings. */
    customColors?: ColorEntry[];

    /** A rich color editing model. */
    colorDictionary?: ColorDictionary;
}

export interface ColorPair {
    foreground: string;
    background: string;
}

/** (Deprecated) Legacy Compat API… We don’t want 3rd parties dealing with this mess. They can support their own color customization without touching this. */
export interface FeatureColorProvider {
    getFeatureColor(key: string, isSubdued?: boolean): ColorPair;
}

/** (Deprecated) Manages Colors of Charts */
export interface ColorDictionary {
    toColorEntries(): ColorEntry[];
    getColorPair(key: string, colorIndex: number, isSubduedPalette?: boolean): ColorPair;
    /** Allows the caller to add color customization for the specified color */
    setColorPair(key: string, colorPair: ColorPair): void;
    /**Indicates if a user-customized Color Pair is associated with the requested key */
    hasCustomColorPair(key: string): boolean;
    /** Indicates if any user-customizations of color pairs are present. */
    hasCustomColors(): boolean;
    clearCustomColors(): void;
}

/**
 * A key value pair for a user selected color preference.
 * Foreground color is omitted because it is mapped from matching background Color.
 */
export interface ColorEntry {
    /** The key for the color entry */
    value: string;

    /** The background hex color value for this entry */
    backgroundColor: string;
}

/** Provides context about the click event.
 *  Note: Data guarantees for the shape of event are very limited due to extensive diversity of chart types and input event formats.
 *  Consumers of these events must exercise care in handling incoming events, as subsets of properties can be omitted for some scenarios.
 */
export interface ClickEvent {
    /** The Chart control which originate this event. */
    sourceChart: any;

    /** The x-axis label entry associated with the evented item */
    labelName?: string;

    /** The name of the series of the evented item */
    seriesName?: string;

    /** The index of the clicked target in the series data array */
    seriesDataIndex?: number;

    /** The custom data that is defined as part of the series */
    seriesCustomData?: any;

    /** The name of this specific item */
    itemName?: string;

    /** The x-value or X-axis array position of the evented item*/
    itemX: number;

    /** The y-value or y-axis array index of the evented item */
    itemY?: number;

    /** z-value of the evented item, in 3-tuple views such as Pivot Table. */
    itemZ?: number;

    /** Browser-space X-coordinate of the event */
    clientX?: number;

    /** Browser-space Y-coordinate of the event */
    clientY?: number;

    /** The mouse button user clicked (as obtained from a JQuery MouseEvent: 1 = left, 2 = middle, 3 = right) */
    button?: number;

    /** True if the ctrl key is pressed */
    ctrlKey?: boolean;

    index?: number;

    userClickedLabel?: string;
    isOtherTabClicked?: boolean;
}

export interface LegendClickEvent {
    // Add parameters here when needed
}

export interface LegendOptions {
    /** Opt-out option for suppressing rendering of legend. */
    enabled?: boolean;

    /** Opt-in option for restricting Legend label sizes. False by default.*/
    limitLabelSize?: boolean;

    /** Opt-in option for placing legend as a right aligned vertical stack. */
    /** This option is maintained for legacy compat purposes only. */
    /** The following options should be used henceforth: stackVertically */
    rightAlign?: boolean;

    /** Opt-in option for placing legend as a vertical stack. False by default.*/
    stackVertically?: boolean;

    /** Opt-in option for alignment of the horizontal stack.*/
    /** Valid values are: 'left', 'right', 'center' */
    /** Note that this option is not supported if 'stackVertically' option is true.*/
    align?: string;
}

export interface TooltipOptions {
    /** (Opt-in) If set to false, It disables the tooltip and mouse tracking. Defaults to true. */
    enabled?: boolean;

    /** Opt-in option for limiting tooltips to just the selected series. This is relevant for "busy" charts which have a large/unwieldly set of series (e.g. CFD).*/
    onlyShowFocusedSeries?: boolean;

    /** Opt-in to reverse the display order of the tooltip series. */
    reverseSeries?: boolean;

    /** Mapping function to convert data points to the line items used for building the tooltip. */
    customTooltipMapping?: (points: DataPoint[]) => TooltipLineItem[];

    /** Formatter to override the header text in the tooltip. */
    customTooltipHeader?: (category: string | number) => string | number;
}

/** A representation of a data point passed to customTooltipMapping in TooltipOptions if one is defined */
export interface DataPoint {
    /**
     * The values of the data point provided in an array. Most charts will only have one value in the array.
     * AreaRange charts pack 2 items of data: the yAxis low value and the yAxis high value.
     */
    values: (string | number)[];

    /** The name of the series the data point is a part of. */
    seriesName: string;

    /** The color associated with the data point. */
    color: string;

    /**
     * Custom data provided in the series.
     * This data is passed along to customTooltipMapping for building the tooltip.
     * Useful for building custom tooltips with additional information not provided by the chart SDK.
     */
    seriesCustomData?: any;

    /**
     * The index of the data point in the series data values array.
     * Useful for looking up a particular value in seriesCustomData if, for example, the
     * custom data provided is an array of values, each being an object associated with an individual
     * series data point.
     */
    seriesDataIndex: number;
}

export interface TooltipLineItem {
    /**
     * The style of the colored marker for the line item.
     * If omitted, only the text will be printed.
     */
    styleType?: TooltipStyleType;

    /** The text to print in the tooltip for this line item. */
    text: string;
}

export interface TooltipStyleType {
    /** The color of the line item marker */
    color: string;

    /** Render custom style of the line item marker */
    renderCustomStyle?: (tooltipStyleType: TooltipStyleType) => string;

    /** Custom tooltip data necessary for rendering */
    customTooltipData?: { [key: string]: any }; // IDictionaryString<any>

    /** The type of line item marker. If type or renderCustomStyle is not specified, type defaults to Bar. */
    type?: TooltipLineItemMarkerType;
}

export const enum TooltipLineItemMarkerType {
    Bar,
    Square
}

/** Chart options for use only when rendering a pie chart */
export interface PieChartOptions {
    /**
     * Opt-in parameter to show aggregrate total value in center of pie and individual data labels.
     * Note: The centered label placement is tested against auto-centered and absolutely positioned pies.
     * Any unusual layout scenarios should be aggressively tested.
     * A value of null or undefined shows the slice data labels but not the value in the center of the pie.
     * A value of true shows the slice data labels and shows the value in the center of the pie.
     * A value of false hides the slice data labels and hides the value in the center of the pie.
     */
    showLabels?: boolean;

    /**
     * The size of the outer pie, expressed in pixels or percent of available chart space.
     */
    size?: string;

    /**
     * The size of the inner diameter for the pie. A size greater than 0 renders a donut chart.
     * Percentage values are relative to the pie size.
     */
    innerSize?: string;

    /**
     * Absolutely positioned center of pie chart, expressed in pixels. Opt-in.
     * Default position is visual center, discounting for space given to the legend, can be expressed as [null, null].
     * This is significant for charts which need consistent positioning, regardless of variable legend size.
     *
     */
    center?: number[];
}

/** Chart options for use only when rendering a line chart */
export interface LineChartOptions {
    /**
     * For line chart, this is opt-in for showing point markers
     * This will be moved to specialized options
     */
    includeMarkers?: boolean;

    /**
     * A separate color for the graph line. By default the line takes the color of the series,
     * but the lineColor setting allows setting a separate color for the line without altering the fillColor.
     */
    lineColor?: string;

    /**
     * Line Width for the graph line , default value is 1
     */
    lineWidth?: number;
}

/** Chart options for use only when rendering a hybrid chart*/
export interface HybridChartOptions {
    /** A list of chart types associated to each series.
     * The type selected here determines the type used for rendering each corresponding series.
     * If this array is incomplete, line type is applied by default.
     */
    chartTypes: string[];
}

/** Chart options for use only when rendering an area chart */
export interface AreaChartOptions extends LineChartOptions {
    /**
     * Gradient color option instead of solid color
     */
    gradientFill?: GradientFill;

    /** If true set stacking to undefined else set stacking to normal */
    disableStacking?: boolean;
}

export interface Stops {
    /**
     * position of the gradient
     */
    position: number;

    /**
     * Color at the above position for the gradient
     */
    color: string;

    /**
     * opacity of the color at the above particular position
     */
    opacity?: number;
}

export interface LinearGradient {
    /**
     * Start horizontal position of the gradient. Float ranges 0-1.
     */
    x1: number;
    /**
     * End horizontal position of the gradient. Float ranges 0-1.
     */
    x2: number;
    /**
     * Start vertical position of the gradient. Float ranges 0-1.
     */
    y1: number;
    /**
     * End vertical position of the gradient. Float ranges 0-1.
     */
    y2: number;
}

export interface GradientFill {
    /**
     * Holds an object that defines the start position and the end position relative to the shape.
     */
    linearGradient?: LinearGradient;

    /**
    The first item in each tuple is the position in the gradient, where 0 is the start of the gradient and 1 is the end of the gradient. 
    Multiple stops can be applied. The second item is the color for each stop. The third item is the opacity for each stop 
     **/
    stops?: Stops[];
}

export interface AxisOptions {
    /* A set of labels for identifying the sequences of elements.
    For multi-dimensional charts using a non-numeric independent(x-Axis), this is required.
    Accepted formats:
      -datetime: UTC Milliseconds from 1970
      -plain text (string values are html encoded)
    */
    labelValues?: any[];

    /** A hint for describing how label data from this axis should be formatted for presentation. */
    labelFormatMode?: LabelFormatModes;

    /** Opt-out option for suppressing rendering of labels.
     Labels are enabled by default.*/
    labelsEnabled?: boolean;

    /** Opt-out option for suppressing rendering of axis lines/tick markings.
        Markings are enabled by default.
        This option should really only be used for small-form chart experiences. */
    markingsEnabled?: boolean;

    /** Plain text title for the title. Null implies no title.
     Where it is present, this parameter is html encoded. */
    title?: string;

    /** The minimum value of the axis. If unset, a min value is automatically calculated.
        If the startOnTick option is true, which it is by default, the rendered min value of the axis
        will be a value less than or equal to this value that is as close as possible yet maintains
        an aesthetically pleasing spacing of axis ticks. */
    min?: number;

    /** The maximum value of the axis. If unset, a max value is automatically calculated.
        If the endOnTick option is true, which it is by default, the rendered max value of the axis
        will be a value greater than or equal to this value that is as close as possible yet maintains
        an aesthetically pleasing spacing of axis ticks. */
    max?: number;

    /** A hint property describing a default maximum value to use, which is ignored when the data is larger. */
    softMax?: number;

    /** Opt-in option to update the axis min value when a legend item is shown/hidden.
     NOTE: Currently only respected for the y-axis. */
    updateMinOnLegendClick?: boolean;

    /** Whether to allow decimals in this axis' ticks
     Implementation defaults to true, override as needed*/
    allowDecimals?: boolean;

    /** Optional elements for filling regions of the chart perpendicular to the axis. Currently, only supported for use with X-Axis.  */
    plotBands?: AxisPlotBand[];

    /** Optional labelling elements for annotating points on the axis. Currently, only supported for use with X-Axis.  */
    plotLines?: AxisPlotLine[];

    /** Opt-out option determining if the axis starts on a tick.
     Implementation defaults to true, override as needed */
    startOnTick?: boolean;

    /** Opt-out option determining if the axis ends on a tick..
     Implementation defaults to true, override as needed */
    endOnTick?: boolean;

    /**Opt-in parameter to prevent truncation of label to 11 characters,
     which is on by default. */
    suppressLabelTruncation?: boolean;

    /**
     *  Opt-in parameter to force rendering of chart content to very start and end of the axis, by hinting those portions to renderer out as "omitted".
     *  Default behavior on x-axis provides half a unit of space before and after the chart body.
     *  Note: This mechanism is a workaround, which does not auto-correct for neccessary reaction on dependent (y) axis.
     *  In particular, caller must explicitly ensure min and max y-axis value range accurately & tastefully reflect entire series, as stock implementation does not account for this.
     *
     *  If your scenario doesn't strictly require this, stick with default behavior.
     */
    renderToEdges?: boolean;

    /** Opt-in option determining if the user can zoom by dragging the mouse on this axix */
    canZoom?: boolean;

    /**
     * enable the visibility of the axis.
     * default value is true
     */
    enabled?: boolean;

    /** Options to managed grid lines */
    gridLineOptions?: GirdLineOptions;

    /** Allows for consistently rendering information sourced from x-axis categories (rather than the x-axis tuple member or array position of the series on Date charts).
     *  Also ensures rounding of Y values.
     */
    formatAccessibleLabelsUsingAxisCategories?: boolean;

    /** Allows for rendering axis lines without labels or data
     */
    retainAxisLine?: boolean;
}

export interface GirdLineOptions {
    /** Determines if the grid lines should be enabled or not */
    enabled?: boolean;

    /** The color of the grid lines */
    gridLineColor?: string;
}

/** Describes bands of filled color to be rendered over the chart. */
export interface AxisPlotBand {
    /**
     * The color of the plot band.
     */
    color: string;

    /**
     * Start Position of the band, in axis index units of the chart.
     */
    start: number;
    /**
     * Position of the band, in axis index units of the chart.
     */
    end: number;
}

/** Describes annotation lines to be rendered over the chart. */
export interface AxisPlotLine {
    /**
     * The annotation label to show with the line.
     */
    labelText: string;
    /**
     * The color of the label text.
     * Defaults to black.
     */
    labelColor?: string;
    /**
     * The color of the annotation line.
     * Defaults to black.
     */
    lineColor?: string;
    /**
     * Position of the line, in axis index units of the chart.
     */
    axisPosition: number;
}

/** Options for a chart, which can be auto-regulated by a chart Host. */
export interface ChartHostOptions {
    /** Control Width, expressed in pixels */
    width: number;

    /** Control Height, expressed in pixels */
    height: number;
}

/** The intersection type for data in a series. */
export type SeriesDataType = number | Datum | number[] | FreePointDatum | null;

export interface DataSeries {
    /** Name of the series, for presentation in legend, tooltips and events.
     Where present, names are html encoded.*/
    name?: string;

    /** Color of the series. Where present, this string is html encoded. */
    color?: string;

    /** The data elements in the series. Individual elements can be expressed as numeric, datum type, bubble chart datum type or 3 element arrays. */
    data: SeriesDataType[];

    /** Plot series using dashed style instead of default (solid)  */
    plotDashedStyle?: boolean;

    /** Additional data values that can be assigned to the series. The data is accessible as part of custom tooltip mapping and click events.
     This is useful for rendering addional data not provided by the charting SDK in tooltips or for click events to perform more complex actions. */
    customData?: any[];

    /** Opt-in for mapping the values to the secondary axis of the chart.*/
    useSecondaryAxis?: boolean;

    /** The symbol to use when drawing the marker for the series
     null, "circle", "square", "diamond", "triangle" "triangle-down" or "url(graphic.png)" */
    markerType?: string;

    /** The size to use when drawing a chart marker for the series. */
    markerRadius?: number;

    /** Opt-in for disable the showing of the tooltip, default is true */
    enableTooltip?: boolean;

    /** For "stacked" chart scenarios: A string key for grouping series together for rendering in a common stack, when the key matches. Series using different key here will be rendered as neighboring stacks. */
    stackGroup?: string;
}

/*Datum is for use with Pie Charts. All other charts use number or arrays of numbers, paired with categorization.

Note: While, this makes pie chart easier, it also makes cross-chart data conversions more of a nuisance.*/
export interface Datum {
    /** Name of this datum sample for presentation in legend, tooltips and events */
    name?: string;
    y?: number;
    color?: string;
    dataLabels?: { color: string };
}

export interface FreePointDatum {
    // If either x or y represent non-numbers, then create labelValues for
    // corresponding axis and use index of labelValues for x or y here.
    x?: number;
    y?: number;

    z?: number;
}

export interface AccessibilityOptions {
    /**
     * enable or disable accessibility
     */
    enabled: boolean;
}

/** Strongly typed contract for configuring chart controls. The base contracts are common across charts.
 Note: untrusted string inputs are force encoded(). If your feature requires particular data handling or the ability to reliably round-trip from events, please pre-encode your input.*/
export interface ChartProps {
    /** Page context, which acts as a service provider.  */
    pageContext: IVssPageContext;

    /** Title of the chart (required). Used for describing the chart in accessible rendering mode and in the aria-label of the chart container. */
    title: string;

    /** The type of the chart. Constants are defined in ChartTypes class. */
    chartType: string;

    /**
     * Charts can be updated in-place for streaming-in data additions or simple switching of mode.
     * For more subtle config changes, spinning up a new chart instance reliably guarantees implementation honors new supplied props.
     */
    updateOnChange?: boolean;

    /**Opt-out parameter to prevent animation, which is on by default. */
    suppressAnimation?: boolean;

    /** Handler for click on chart series content. */
    click?: (clickEvent: ClickEvent) => void;

    /** General Presentation options managed by the chart host. e.g width & height */
    hostOptions?: ChartHostOptions;

    /** Handler for chart legend click event. Can return false to supress default behavior.*/
    legendClick?: (clickEvent: LegendClickEvent) => boolean;

    /** Options for configuring the legend. Notably, visibility and placement */
    legend?: LegendOptions;

    /** Options for configuring the Tooltip. */
    tooltip?: TooltipOptions;

    /** If True, margin between the outer edge of the chart and the plot area will be set to 0
     False will use default margin values*/
    suppressMargin?: boolean;

    /** (opt-in) If true, the alternate tabular representation of the chart will be rendered.
        This is a debugging aid to help validate tabular rendering.
        Note: Only minimal "data" centric options are honored in this mode. Visual/layout options are definitively ignored. */
    showAccessibleForm?: boolean;

    /**Container for passing type-specific chart options. */
    specializedOptions?: PieChartOptions | LineChartOptions | HybridChartOptions | AreaChartOptions | FunnelChartOptions | BubbleChartOptions;

    /** Options to configure the "independent" or "category" x-Axis. This is used for presentation of "categories" or "independent variables". */
    xAxis?: AxisOptions;

    /** Options to configure the y-Axis. This is used for presentation of "values" or "dependent variables". */
    yAxis?: AxisOptions;

    /** Options to configure the secondary y-Axis. */
    yAxisSecondary?: AxisOptions;

    /** Data series representation chart Data */
    series: DataSeries[];

    /** Color customization options from user, as managed in the chart editor. */
    colorCustomizationOptions?: ColorCustomizationOptions;

    /**
     * Opt-out for suppressing interpolation of null values in continuous series. When disabled, null points are treated as disjoint ends, as opposed to being smoothly connected.
     */
    interpolateNullValues?: boolean;

    /**
     * Option to enable or disable accessiblity for chart
     */
    accessibility?: AccessibilityOptions;
}

/**
 * Declaration of Chart types rendered from the vss-charts-common implementation.
 * Ultimately, we'll use this for supplanting series scoped typing.
 *
 * To limit script overhead, non-Traditional Chart types are to be exposed through separate projects.
 */
export class ChartTypesConstants {
    public static Pie: string = "pie";

    public static Bar: string = "bar";
    public static StackedBar: string = "stackedBar";

    public static Column: string = "column";
    public static StackedColumn: string = "stackedColumn";

    /*A special case of column chart, which applies a standard set of presentational settings for consistent appearance..*/
    public static Histogram: string = "histogram";

    public static Area: string = "area";
    public static StackedArea: string = "stackedArea";

    public static Line: string = "line";

    public static Scatter: string = "scatter";

    public static Bubble: string = "bubble";

    /** A Hybrid chart which renders the first series as columns, and subsequent series as line chart. */
    public static ColumnLine: string = "columnLine";

    /** A flexible chart which renders the charts using the chart types specified via the customOptions.chartTypes array. */
    public static Hybrid: string = "hybrid";

    /** Implements an area-range chart for expressing bounded ranges.
     * REQUIRES inclusion of "vss-charts-arearange" to render. */
    public static AreaRange: string = "arearange";

    /** A styled table which constrains itself to available fixed size allocation.
     * Rows & Columns are given variable proportional subdivisions.
     * REQUIRES inclusion of "vss-charts-table" to render. */
    public static Table: string = "table";

    /** A Funnel visualization.
     * REQUIRES inclusion of "vss-charts-funnel" to render. */
    public static Funnel: string = "funnel";
}

export class LabelFormatModes {
    // TODO: Discuss our default behavior. In practice, Textual is the default.

    /* Suitable for categorical presentation of discrete named elements e.g. "apples", "oranges" */
    public static Textual: string = "textual";

    /* Suitable for presentation of continuous numerical axes. */
    public static Linear: string = "linear";

    /** Formats chart dates at granularity of day-month scale. When using this label format mode, the date must be an actual valid date object or a date string in the YYYY-MM-DD format.
     *  Other date string formats are not currently supported in the charting stack and may display NaN
     */
    public static DateTime_DayInMonth: string = "dateTime_DayInMonth";

    /** Formats the number of seconds in the hh:mm:ss format.*/
    public static NumberSeconds_HourMinuteSeconds: string = "numberSeconds_HourMinuteSeconds";

    /** Formats numbers as percentages. e.g. 10%, 30% */
    public static Percentage: string = "percentage";
}

export class ChartColorizer {
    public static Transparent: string = "rgba(255,255,255,0)";
}

/** Chart options for use only when rendering a funnel chart */
export interface FunnelChartOptions {
    /**
     * The height of the neck, the lower part of the funnel. A number defines pixel width, a percentage string defines a
     * percentage of the plot area height.
     * @default '25%'
     */
    neckHeight?: number | string;

    /**
     * The width of the neck, the lower part of the funnel. A number defines pixel width, a percentage string defines a
     * percentage of the plot area width.
     * @default '30%'
     */
    neckWidth?: number | string;

    /**
     * The height of the funnel. If it is a number it defines the pixel height, if it is a percentage string
     * it is the percentage of the plot area height.
     */
    height?: number | string;

    /**
     * The width of the funnel compared to the width of the plot area, or the pixel width if it is a number.
     * @default '90%'
     */
    width?: number | string;
}

/** Chart options for use when rendering a bubble chart */
export interface BubbleChartOptions {
    /**
     * The name of the measure used to represent 'z' values in Bubble chart data. The value
     * representing this label translates to the size of the bubble
     */
    measureName: string;
}
